import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/gmao";
import { useAuth } from "../lib/auth";
import { Modal, Btn } from "../components/Shared";
import { toast } from "sonner";

export const APARIENCIA_DEFAULT = { contraste: "normal", densidad: "normal", menu: "expandido", modo: "claro" };

export const PREVIEW_KEY = "gmao_tema_preview";
let temaCorporativo = localStorage.getItem("gmao_tema_corporativo") || "FD";
const PUBLIC_PATHS = ["/login", "/activo/", "/oferta/"];
const mqDark = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;

// claro | oscuro | auto → modo efectivo. Solo dentro de la app con sesión (nunca en login ni portales públicos).
export const resolveModo = (ap) => {
  if (!ap || PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p))) return "claro";
  const m = ap.modo || "claro";
  return m === "oscuro" || (m === "auto" && mqDark?.matches) ? "oscuro" : "claro";
};

// Aplica el tema corporativo (FOR DECI) + las preferencias del usuario como atributos de <html>. Los PDF no cambian.
// Si hay una vista previa de tema en sessionStorage (solo admin, solo esta pestaña), prevalece sobre el corporativo.
export const applyApariencia = (ap, tema = temaCorporativo) => {
  const el = document.documentElement;
  el.dataset.tema = sessionStorage.getItem(PREVIEW_KEY) || tema || "FD";
  const a = { ...APARIENCIA_DEFAULT, ...(ap || {}) };
  if (a.contraste === "alto") el.dataset.contraste = "alto"; else delete el.dataset.contraste;
  if (a.densidad === "compacta") el.dataset.densidad = "compacta"; else delete el.dataset.densidad;
  if (a.menu === "compacto") el.dataset.menu = "compacto"; else delete el.dataset.menu;
  if (ap && resolveModo(a) === "oscuro") el.dataset.modo = "oscuro"; else delete el.dataset.modo;
};

export function useApariencia() {
  const { user } = useAuth();
  const loc = useLocation();
  useEffect(() => { applyApariencia(user ? user.apariencia : null); }, [user, loc.pathname]);
  useEffect(() => {
    if (!mqDark || user?.apariencia?.modo !== "auto") return;
    const h = () => applyApariencia(user.apariencia);
    mqDark.addEventListener("change", h);
    return () => mqDark.removeEventListener("change", h);
  }, [user]);
  useEffect(() => {
    api.get("/public/settings").then((r) => {
      const t = r.data?.tema_corporativo || "FD";
      if (t !== temaCorporativo) { temaCorporativo = t; localStorage.setItem("gmao_tema_corporativo", t); applyApariencia(user ? user.apariencia : null); }
    }).catch(() => {});
  }, []); // eslint-disable-line
}

export const setTemaPreview = (t) => { if (t) sessionStorage.setItem(PREVIEW_KEY, t); else { sessionStorage.removeItem(PREVIEW_KEY); sessionStorage.removeItem("gmao_fd_tone"); } window.dispatchEvent(new Event("gmao:tema-preview")); };
export const FD_TONES = [["original", "Original", "#DFDECE"], ["suau", "Suau", "#E8E3D9"], ["molt_suau", "Molt suau", "#F1F0E8"]];
export const applyFdTone = () => {
  const tone = FD_TONES.find((x) => x[0] === sessionStorage.getItem("gmao_fd_tone")) || FD_TONES[1];
  document.documentElement.style.setProperty("--fd-bg", tone[2]);
};

// Barra flotante mientras se previsualiza una propuesta de tema (no se guarda en el servidor).
export function TemaPreviewBar() {
  const { user } = useAuth();
  const [t, setT] = useState(() => sessionStorage.getItem(PREVIEW_KEY) || "");
  const [tone, setTone] = useState(() => sessionStorage.getItem("gmao_fd_tone") || "suau");
  useEffect(() => {
    const h = () => { setT(sessionStorage.getItem(PREVIEW_KEY) || ""); applyApariencia(user?.apariencia); applyFdTone(); };
    window.addEventListener("gmao:tema-preview", h);
    applyFdTone();
    return () => window.removeEventListener("gmao:tema-preview", h);
  }, [user]);
  if (!t) return null;
  const pick = (k) => { sessionStorage.setItem("gmao_fd_tone", k); setTone(k); applyFdTone(); };
  return (
    <div className="fd-preview-bar" data-testid="tema-preview-bar">
      <span>Vista prèvia · <strong>Proposta FOR DECI</strong></span>
      <span style={{ opacity: 0.8 }}>To del fons:</span>
      {FD_TONES.map(([k, l, c]) => <button key={k} type="button" onClick={() => pick(k)} style={{ background: tone === k ? "#E9EDF1" : "transparent", color: tone === k ? "#202830" : "#fff", border: `1px solid ${c}` }} data-testid={`fd-tone-${k}`}>{l} <span style={{ fontFamily: "monospace", fontWeight: 400 }}>{c}</span></button>)}
      <button type="button" onClick={() => setTemaPreview("")} data-testid="tema-preview-exit">Sortir</button>
    </div>
  );
}

const Seg = ({ value, onChange, opts, testid }) => (
  <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden text-xs" data-testid={testid}>
    {opts.map(([v, l], k) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
        className={`px-3 py-1.5 ${value === v ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"} ${k ? "border-l border-slate-300" : ""}`}>{l}</button>
    ))}
  </div>
);

// Configuración → Apariencia (por usuario). Vista previa inmediata; se guarda en el servidor al pulsar Guardar.
export function AparienciaModal({ open, onClose }) {
  const { user, setUser } = useAuth();
  const [ap, setAp] = useState(APARIENCIA_DEFAULT);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setAp({ ...APARIENCIA_DEFAULT, ...(user?.apariencia || {}) }); }, [open, user]);
  const set = (k, v) => { const n = { ...ap, [k]: v }; setAp(n); applyApariencia(n); };
  const cancel = () => { applyApariencia(user?.apariencia); onClose(); };
  const save = async (val = ap) => {
    setBusy(true);
    try {
      const { data } = await api.put("/users/me/apariencia", val);
      setUser({ ...user, apariencia: data });
      applyApariencia(data);
      toast.success("Apariencia guardada para tu usuario.");
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "No se pudo guardar la apariencia."); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={cancel} title="Apariencia y diseño">
      <div className="space-y-5" data-testid="apariencia-modal">
        <p className="text-xs text-slate-500">Tema corporativo <strong>FOR DECI · Industrial profesional</strong>. Estas opciones son solo tuyas: no cambian la apariencia de otros usuarios ni los PDF.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><div className="text-xs font-bold text-slate-800 mb-1.5">Modo</div><Seg value={ap.modo} onChange={(v) => set("modo", v)} opts={[["claro", "Claro"], ["oscuro", "Oscuro"], ["auto", "Sistema"]]} testid="apariencia-modo" /><div className="text-[11px] text-slate-500 mt-1">Oscuro: fondo y menú oscuros con paneles claros. Sistema: sigue la preferencia del dispositivo. No afecta al login ni a los portales públicos.</div></div>
          <div><div className="text-xs font-bold text-slate-800 mb-1.5">Contraste</div><Seg value={ap.contraste} onChange={(v) => set("contraste", v)} opts={[["normal", "Normal"], ["alto", "Alto"]]} testid="apariencia-contraste" /><div className="text-[11px] text-slate-500 mt-1">Alto: cabeceras de tabla en azul grafito, bordes más marcados y filas alternas.</div></div>
          <div><div className="text-xs font-bold text-slate-800 mb-1.5">Densidad</div><Seg value={ap.densidad} onChange={(v) => set("densidad", v)} opts={[["normal", "Normal"], ["compacta", "Compacta"]]} testid="apariencia-densidad" /><div className="text-[11px] text-slate-500 mt-1">Compacta: menos espacio y textos algo menores para ver más filas.</div></div>
          <div><div className="text-xs font-bold text-slate-800 mb-1.5">Menú lateral</div><Seg value={ap.menu} onChange={(v) => set("menu", v)} opts={[["expandido", "Expandido"], ["compacto", "Compacto"]]} testid="apariencia-menu" /><div className="text-[11px] text-slate-500 mt-1">Compacto: solo iconos (en pantallas grandes).</div></div>
        </div>
        <div className="flex flex-wrap justify-between gap-2 pt-3 border-t border-slate-100">
          <Btn variant="secondary" type="button" onClick={() => { setAp(APARIENCIA_DEFAULT); applyApariencia(APARIENCIA_DEFAULT); save(APARIENCIA_DEFAULT); }} disabled={busy} data-testid="apariencia-restaurar">Restaurar diseño predeterminado</Btn>
          <div className="flex gap-2">
            <Btn variant="secondary" type="button" onClick={cancel} disabled={busy}>Cancelar</Btn>
            <Btn type="button" onClick={() => save()} disabled={busy} data-testid="apariencia-guardar">{busy ? "Guardando…" : "Guardar"}</Btn>
          </div>
        </div>
        {user?.role === "admin" && sessionStorage.getItem(PREVIEW_KEY) && (
          <div className="pt-3 border-t border-slate-100 text-xs text-slate-600 flex flex-wrap items-center justify-between gap-2">
            <span>Estás en una vista previa de tema. Cierra la barra inferior para volver al tema corporativo.</span>
          </div>
        )}
      </div>
    </Modal>
  );
}
